import './src/polyfills';
import React from 'react';
import { StatusBar } from 'expo-status-bar';
import { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, Text, TextInput, ScrollView, Platform, useWindowDimensions, TouchableOpacity, Image, ActivityIndicator, SafeAreaView, Modal, Button, Switch } from 'react-native';
import { getDefaultLanguage, API_URL, API_ENDPOINTS } from './config/localization';
import { getTranslation } from './src/i18n/translations';
import { Main } from './src/components/audiocall/main';
import { AudioProvider, useAudio } from './src/services/audio/AudioContext';
import { AudioStateProvider } from './src/contexts/AudioContext';
import { v4 as uuid } from 'uuid';
import { ChatHeader } from './src/components/ChatHeader';
import { ChatInput } from './src/components/ChatInput';
import { ChatMessages } from './src/components/ChatMessages';
import { TutorProvider, useTutor } from './src/contexts/TutorContext';
import { ChatProvider } from './src/contexts/ChatContext';
import { SettingsProvider } from './src/contexts/SettingsContext';
import { AnimatedLogo } from './src/components/AnimatedLogo';

// Debug environment configuration

const DEFAULT_LANGUAGE = getDefaultLanguage();
interface UserInfo {
  languageLevel: string;
  age: number;
  conversationType: string;
  speechSpeed: string;
  nativeLanguage: string;
  autoPlayAudio: boolean;
}

interface Message {
  id: number;
  text: string;
  type: 'user' | 'bot';
  audioUrl?: string;
  timestamp: number;
  language?: LanguageCode;
  isProcessing?: boolean;
  tutor?: Tutor;  // Add tutor to store which tutor generated this message
}

interface AppState {
  messages: Message[];
  textMessage: string;
  isRecording: boolean;
  isProcessing: boolean;
  status: string;
  selectedLanguage: LanguageCode;
  interfaceLanguage: LanguageCode;
  isLoading: boolean;
  fontsLoaded: boolean;
  showShortAudioMessage: boolean;
  hasAudioPermission: boolean | null;
  lastMessageTime: number;
  isWaitingForAI: boolean;
  isConfigModalVisible: boolean;
  userInfo: UserInfo;
  showCallModal: boolean;
  isTTSTesting: boolean;
  isLLaMATTSPlaying: boolean;
  currentLLaMAText: string;
  currentlyPlayingId: string | null;
  userPlayingId: string | null;
  error: Error | null;
  sessionId: string;
  selectedTutor: Tutor | null;
  showTutorModal: boolean;
  tutors: Tutor[];
}

interface Tutor {
  id: string;
  name: string;
  language_code: string;
  native_language: string;
  teaching_level: string;
  personality: string;
  teaching_style: string;
  voice_id: string;
  avatar_url: string;
  personal_background: string;
  interests: string[];
  teaching_experience: string;
  specialties: string[];
}

export default function App() {
  useEffect(() => {
    // Add viewport meta tag programmatically
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    document.head.appendChild(viewport);
  }, []);

  return (
    <SettingsProvider>
      <TutorProvider>
        <ChatProvider>
          <AudioStateProvider>
            <AudioProvider apiUrl={API_URL}>
              <AppContent />
            </AudioProvider>
          </AudioStateProvider>
        </ChatProvider>
      </TutorProvider>
    </SettingsProvider>
  );
}

function AppContent() {
  const [sessionId] = useState(() => {
    if (Platform.OS === 'web') {
      const storedId = localStorage.getItem('voicechat_session_id');
      return storedId || uuid();
    }
    return uuid();
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isWaitingForAI, setIsWaitingForAI] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  const [userPlayingId, setUserPlayingId] = useState<string | null>(null);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [showTutorModal, setShowTutorModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { selectedTutor, tutors } = useTutor();
  const scrollViewRef = useRef<ScrollView>(null);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    languageLevel: 'intermediate',
    age: 25,
    conversationType: 'casual',
    speechSpeed: '1',
    nativeLanguage: 'English',
    autoPlayAudio: true
  });

  useEffect(() => {
    if (Platform.OS === 'web') {
      localStorage.setItem('voicechat_session_id', sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Wait a bit to ensure all components are mounted
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing app:', error);
        setIsLoading(false);
      }
    };
    initializeApp();
  }, []);

  const getDefaultTutorForLanguage = useCallback((languageCode: string): Tutor | null => {
    // Implement logic to get default tutor for language
    return null;
  }, []);

  const getAvatarSource = (tutorId: string) => {
    // Get the tutor's profile from the tutors list
    // Implement logic to get tutor's avatar
    return null;
  };

  const getTutorAvatar = useCallback((tutor: Tutor | null, languageCode?: string) => {
    // If no tutor is provided, try to get the default tutor for the language
    const effectiveTutor = tutor || (languageCode ? getDefaultTutorForLanguage(languageCode) : null);
    
    if (!effectiveTutor) {
      console.warn('No tutor available for avatar');
      return null;
    }

    try {
      return getAvatarSource(effectiveTutor.id);
    } catch (error) {
      console.warn('Failed to load tutor avatar:', error);
      return null;
    }
  }, [getDefaultTutorForLanguage]);

  const handleTutorSelect = (tutor: Tutor) => {
    // Implement logic to handle tutor selection
  };

  useEffect(() => {
    if (selectedTutor) {
      handleTutorSelect(selectedTutor);
    }
  }, [selectedTutor]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#fff',
    },
    wrapper: Platform.OS === 'web' ? {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: '#F7F7F7',
    } : {
      flex: 1,
      backgroundColor: '#fff',
    },
    mainContainer: {
      flex: 1,
      maxWidth: Platform.OS === 'web' ? 800 : '100%',
      width: '100%',
      backgroundColor: '#ffffff',
      position: 'relative',
      height: Platform.OS === 'web' ? '100vh' : '100%',
      overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
      }),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: '#f0f0f0',
      backgroundColor: '#fff',
      height: Platform.OS === 'web' ? 72 : 64,
      position: 'relative',
      zIndex: 1,
    },
    tutorSelector: {
      flex: 1,
      maxWidth: Platform.OS === 'web' ? 300 : 200,
      zIndex: 2,
    },
    menuButton: {
      marginLeft: 'auto',
    },
    content: {
      flex: 1,
      width: '100%',
     
      paddingBottom: Platform.OS === 'ios' ? 88 : Platform.OS === 'web' ? 70 : 50,
      backgroundColor: '#ffffff',
      ...(Platform.OS === 'web' && {
        overflowY: 'auto',
        height: 'calc(100vh - 140px)',
      }),
    },
    footer: {
      position: Platform.OS === 'web' ? 'fixed' : 'absolute',
      bottom: 0,
      left: Platform.OS === 'web' ? '50%' : 0,
      right: Platform.OS === 'web' ? 'auto' : 0,
      transform: Platform.OS === 'web' ? [{translateX: '-50%'}] : [],
      width: '100%',
      maxWidth: Platform.OS === 'web' ? 800 : '100%',
      backgroundColor: '#fff',
      zIndex: 1000,
      borderTopWidth: 1,
      borderTopColor: '#e0e0e0',
      paddingBottom: Platform.OS === 'ios' ? 34 : 0,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)',
      }),
    },
    chatHeader: {
      width: '100%',
      zIndex: 1,
    },
    inputContainer: {
      width: '100%',
      zIndex: 1,
    },
    messageContainer: {
      flex: 1,
      width: '100%',
      // paddingTop: Platform.OS === 'web' ? 60 : 50, // Space for input container
      // paddingBottom: 20,
      paddingHorizontal: 5,
      backgroundColor: '#ffffff',
    },
    messagesContent: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
    },
    languageScrollContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: '100%',
      whiteSpace: 'nowrap',
      gap: 8,
      paddingLeft: 2,
      paddingRight: 16,
      width: '100%',
    },
    messageCard: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      padding: 15,
      maxWidth: '80%',
      minWidth: '40%',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    userMessageCard: {
      backgroundColor: '#007AFF',
      borderRadius: 12,
      padding: 15,
      maxWidth: '80%',
      minWidth: '40%',
      marginLeft: 'auto',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    inputDisabled: {
      backgroundColor: '#f5f5f5',
      borderColor: '#e0e0e0',
      color: '#999999',
    },
    sendButton: {
      backgroundColor: '#007AFF',
      borderRadius: 25,
      width: 50,
      height: 50,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    recordButton: {
      backgroundColor: isRecording ? '#FF3B30' : '#007AFF',
      borderRadius: 25,
      width: 50,
      height: 50,
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 10,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    message: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 4,
      gap: 3,
    },
    userMessage: {
      flexDirection: 'row-reverse',
      alignSelf: 'flex-end',
      marginRight: 5,
      paddingLeft: 5,
    },
    assistantMessage: {
      alignSelf: 'flex-start',
      backgroundColor: '#f8f9fa',
      borderRadius: 20,
    },
    shortAudioMessage: {
      position: 'absolute',
      top: -30,
      left: 0,
      right: 0,
      backgroundColor: '#ff6b6b',
      padding: 5,
      borderRadius: 5,
      marginHorizontal: 15,
    },
    shortAudioText: {
      color: '#ffffff',
      textAlign: 'center',
      fontSize: 14,
    },
    inputContent: {
      width: '100%',
      maxWidth: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginHorizontal: 'auto',
    },
    mainContent: {
      flex: 1,
      position: 'relative',
    },
    controls: {
      padding: 8,
      paddingBottom: Platform.OS === 'ios' ? 20 : 8,
      backgroundColor: '#ffffff',
      borderTopWidth: 1,
      borderTopColor: '#e0e0e0',
      position: Platform.OS === 'web' ? 'sticky' : 'relative',
      bottom: 0,
      zIndex: 10,
      ...(Platform.OS === 'web' && {
        width: '100%',
        maxWidth: '100%',
      }),
      boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.05)',
    },
    languageScroll: {
      flexGrow: 0,
      width: '100%',
    },
    languageButton: {
      flexDirection: 'row',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 16,
      backgroundColor: '#ffffff',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      minWidth: 70,
      marginBottom: 2,
    },
    selectedLanguageButton: {
      backgroundColor: '#0A84FF',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    languageButtonText: {
      fontSize: 11,
      color: '#666',
      textAlign: 'center',
      fontWeight: '500',
      letterSpacing: 0.3,
    },
    selectedLanguageButtonText: {
      color: '#ffffff',
      fontWeight: '600',
    },
    flagIcon: {
      width: 22,
      height: 22,
      borderRadius: 11,
      backgroundColor: '#f0f0f0',
      overflow: 'hidden',
    },
    flagImage: {
      width: '120%',
      height: '120%',
      marginLeft: '-10%',
      marginTop: '-10%',
    },
    messageRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 5,
      gap: 3,
    },
    userMessageRow: {
      justifyContent: 'flex-end',
    },
    avatar: {
      width: 96,
      height: 96,
      borderRadius: 48,
      alignSelf: 'center',
      marginLeft: 0,
    },
    messageText: {
      fontSize: 14,
      color: '#212529',
      flexWrap: 'wrap',
      marginBottom: message => message.audioUrl ? 8 : 0,
    },
    audioContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginTop: 8,
      paddingHorizontal: 8,
    },
    playButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
      borderRadius: 12,
      paddingVertical: 4,
      paddingHorizontal: 0,
    },
    playButtonIcon: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: '#007AFF',
      justifyContent: 'center',
      alignItems: 'center',
    },
    userPlayButtonIcon: {
      backgroundColor: '#0A84FF',
    },
    playButtonText: {
      marginLeft: 6,
      fontSize: 12,
      color: '#666',
    },
    recordingText: {
      flex: 1,
      backgroundColor: '#ffffff',
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 8,
      color: '#6C757D',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    actionButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: '#005bb8',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    recordingButton: {
      backgroundColor: '#b650b2',
    },
    disabledButton: {
      opacity: 0.5,
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
      marginHorizontal: 4,
    },
    processingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 8,
      gap: 8,
    },
    processingText: {
      color: '#666',
      fontSize: 14,
      fontStyle: 'italic',
    },
    tutorModalContent: {
      backgroundColor: '#fff',
      borderRadius: 12,
      padding: 24,
      width: '90%',
      maxWidth: 400,
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
    },
    tutorModalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
    },
    tutorItem: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      backgroundColor: '#f8f9fa',
      borderRadius: 12,
      marginBottom: 8,
    },
    selectedTutorItem: {
      backgroundColor: '#e3f2fd',
    },
    tutorAvatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
      marginRight: 16,
    },
    tutorInfo: {
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
    },
    tutorName: {
      fontSize: 16,
      fontWeight: '600',
      color: '#333',
      marginBottom: 4
    },
    tutorSpecialty: {
      fontSize: 14,
      color: '#666'
    },
    tutorLanguage: {
      fontSize: 14,
      color: '#4CAF50',
      fontWeight: '500',
      marginBottom: 4
    },
    loadingContainer: {
      flex: 1,
      backgroundColor: '#ffffff',
      minHeight: Platform.OS === 'web' ? '100vh' : '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      marginTop: 16,
      fontSize: 16,
      color: '#333333',
      fontWeight: '500',
      textAlign: 'center',
    },
  });

  if (isLoading || !tutors.length) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <AnimatedLogo />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <View style={styles.wrapper}>
      <SafeAreaView style={styles.mainContainer}>
        <StatusBar style="dark" />
        <View style={styles.header}>
          <View style={styles.tutorSelector}>
            <ChatHeader
              selectedLanguage={selectedLanguage}
              getTutorAvatar={getTutorAvatar}
              setShowTutorModal={setShowTutorModal}
              setShowCallModal={setShowCallModal}
              showTutorModal={showTutorModal}
              isConfigModalVisible={isConfigModalVisible}
              setIsConfigModalVisible={setIsConfigModalVisible}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              setInterfaceLanguage={setSelectedLanguage}
              selectedTutor={selectedTutor}
            />
          </View>
          <View style={styles.menuButton}>
            {/* MenuButton */}
          </View>
        </View>
        
        <View style={styles.content}>
          <ChatMessages
            getTranslation={getTranslation}
            tutor={selectedTutor}
          />
        </View>

        <View style={styles.footer}>
          <ChatInput
            messages={messages}
            setMessages={setMessages}
            sessionId={sessionId}
            userInfo={userInfo}
            selectedLanguage={selectedLanguage}
            getTranslation={getTranslation}
            openMenuWindow={() => setIsConfigModalVisible(true)}
          />
        </View>

        <Modal
          visible={showCallModal}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setShowCallModal(false)}
        >
          <SafeAreaView style={styles.modalContainer}>
            <Main
              visible={showCallModal}
              onClose={() => setShowCallModal(false)}
            />
          </SafeAreaView>
        </Modal>
      </SafeAreaView>
    </View>
  );
}
